import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, RefreshCw, Languages, Check, Sparkles, Undo2, Redo2 } from 'lucide-react';
import { Editor } from '@tiptap/react';

interface FloatingActionBarProps {
  onActionClick: (action: 'copy' | 'rewrite' | 'translate') => void;
  editor: Editor | null;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

const FloatingActionBar: React.FC<FloatingActionBarProps> = ({ onActionClick, editor, onUndo, onRedo, canUndo = false, canRedo = false }) => {
  const [copiedTooltip, setCopiedTooltip] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const handleCopy = () => {
    onActionClick('copy');
    setCopiedTooltip(true);
    setTimeout(() => setCopiedTooltip(false), 2000);
  };

  const buttons = [
    {
      id: 'copy',
      icon: copiedTooltip ? Check : Copy,
      label: copiedTooltip ? 'Copied!' : 'Copy',
      onClick: handleCopy,
      gradient: 'from-gray-50 to-gray-100',
      hoverGradient: 'from-gray-100 to-gray-200',
      iconColor: 'text-gray-600',
      tooltip: 'Copy to clipboard',
    },
    {
      id: 'rewrite',
      icon: RefreshCw,
      label: 'Rewrite',
      onClick: () => onActionClick('rewrite'),
      gradient: 'from-purple-50 to-purple-100',
      hoverGradient: 'from-purple-100 to-purple-200',
      iconColor: 'text-purple-600',
      tooltip: 'AI rewrite options',
      sparkle: true,
    },
    {
      id: 'translate',
      icon: Languages,
      label: 'Translate',
      onClick: () => onActionClick('translate'),
      gradient: 'from-blue-50 to-blue-100',
      hoverGradient: 'from-blue-100 to-blue-200',
      iconColor: 'text-blue-600',
      tooltip: 'Translate to another language',
    },
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 0.4,
        }}
        className="fixed bottom-4 sm:bottom-6 md:bottom-8 left-0 right-0 z-50 flex justify-center px-4"
      >
        <motion.div
          className="bg-white/90 backdrop-blur-2xl rounded-full shadow-2xl overflow-hidden p-1.5 sm:p-2 max-w-full overflow-x-auto"
          style={{
            boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.03)',
          }}
        >
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Undo/Redo buttons */}
            <div className="flex items-center space-x-0.5 sm:space-x-1 mr-1 sm:mr-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-50 rounded-full">
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.45,
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onUndo}
                disabled={!canUndo}
                className={`p-1.5 sm:p-2 rounded-full transition-all ${
                  canUndo
                    ? 'hover:bg-gray-200 text-gray-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Undo"
              >
                <Undo2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" strokeWidth={2} />
              </motion.button>

              <div className="w-px h-4 sm:h-5 bg-gray-200" />

              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.48,
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onRedo}
                disabled={!canRedo}
                className={`p-1.5 sm:p-2 rounded-full transition-all ${
                  canRedo
                    ? 'hover:bg-gray-200 text-gray-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Redo"
              >
                <Redo2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" strokeWidth={2} />
              </motion.button>
            </div>
            
            {buttons.map((button, index) => (
              <motion.div
                key={button.id}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.5 + index * 0.05,
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                }}
                className="relative"
                onMouseEnter={() => setHoveredButton(button.id)}
                onMouseLeave={() => setHoveredButton(null)}
              >
                {/* Tooltip - removed for cleaner experience */}

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={button.onClick}
                  className={`relative px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 rounded-full bg-gradient-to-r ${
                    hoveredButton === button.id ? button.hoverGradient : button.gradient
                  } transition-all duration-200 group flex items-center justify-center`}
                >
                  <div className="flex items-center justify-center space-x-0 sm:space-x-2">
                    {/* Sparkle Effect for AI features */}
                    {button.sparkle && (
                      <motion.div
                        className="absolute -top-1 -right-1"
                        animate={{
                          rotate: [0, 180, 360],
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          repeatDelay: 2,
                        }}
                      >
                        <Sparkles className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-purple-500" strokeWidth={2} />
                      </motion.div>
                    )}

                    {/* Icon with animation */}
                    <motion.div
                      className="flex items-center justify-center"
                      animate={button.id === 'rewrite' && hoveredButton === 'rewrite' ? {
                        rotate: 360,
                      } : button.id === 'translate' && hoveredButton === 'translate' ? {
                        x: [0, 2, -2, 0],
                      } : {}}
                      transition={{
                        duration: button.id === 'rewrite' ? 0.5 : 0.3,
                      }}
                    >
                      <button.icon className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${button.iconColor}`} strokeWidth={2} />
                    </motion.div>

                    {/* Label - hide on smallest screens */}
                    <span className={`hidden sm:inline text-xs sm:text-sm font-medium ${button.iconColor.replace('text-', 'text-')}`}>
                      {button.label}
                    </span>
                  </div>

                  {/* Success animation for copy */}
                  <AnimatePresence>
                    {button.id === 'copy' && copiedTooltip && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="absolute inset-0 flex items-center justify-center rounded-full bg-gradient-to-r from-green-50 to-green-100"
                      >
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-green-600" strokeWidth={2} />
                          <span className="hidden sm:inline text-xs sm:text-sm font-medium text-green-600">Copied!</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Subtle glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-400/20 via-purple-400/20 to-blue-400/20 blur-3xl -z-10"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      {/* Copied Success Toast - Separated from button */}
      <AnimatePresence>
        {copiedTooltip && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 25,
            }}
            className="fixed top-32 sm:top-24 left-0 right-0 flex justify-center z-50"
          >
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center space-x-1.5 sm:space-x-2 shadow-lg">
              <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4" strokeWidth={2} />
              <span className="text-xs sm:text-sm font-medium">Text copied to clipboard</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingActionBar;